/**
 * Notification service - Web push notifications
 */

import { getCurrentPeriod } from '../utils/time.js';

class NotificationService {
  private permission: NotificationPermission = 'default';

  constructor() {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    try {
      this.permission = await Notification.requestPermission();
      return this.permission === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  /**
   * Get current permission status
   */
  getPermission(): NotificationPermission {
    return this.permission;
  }

  /**
   * Show a notification at timer boundary
   */
  showBoundaryNotification(): void {
    if (this.permission !== 'granted') {
      return;
    }

    const currentPeriod = getCurrentPeriod();
    const isRest = currentPeriod.type === 'rest';

    const title = isRest ? 'Rest Period!' : 'Activity Period!';
    const body = isRest
      ? 'Take a 5-minute break'
      : 'Ready to start your next 25-minute focus session?';

    try {
      const notification = new Notification(title, {
        body,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: 'pomodoro-boundary',
        requireInteraction: true,
        data: {
          type: 'boundary',
          periodType: currentPeriod.type
        }
      });

      // Focus app when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }

  /**
   * Show a custom notification
   */
  showNotification(title: string, body: string, options?: NotificationOptions): void {
    if (this.permission !== 'granted') {
      return;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: '/icons/icon-192x192.png',
        ...options
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
